import React, { useState, useEffect } from "react";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { usePrice } from "@/lib/price";

const toolsMenu = [
  { label: "Playbook Directory", caption: "Hundreds of grounded playbooks", href: "/playbooks" },
  { label: "Job Directory", caption: "338,947 live jobs · 18K boards", tag: "LIVE", href: "/jobs" },
  { label: "Resume Builder", caption: "A role-ready resume in minutes", href: "/resume" },
  { label: "Tool Directory", caption: "252 tools, prompts, repos & GPTs", href: "/toolbox" },
  { label: "Readiness Simulator", caption: "Score & sharpen your interview", href: "/simulator" },
  { label: "OmniCut", caption: "Frame-by-frame viral edit blueprints", href: "/omnicut" },
  { label: "OmniScript", caption: "AI scripts for your videos", tag: "SOON", href: "/omniscript" },
  { label: "Affiliate", caption: "Share the stack · keep 50%", tag: "EARN", href: "/affiliate" },
];

function ToolsDropdown() {
  return (
    <div className="relative group">
      <button
        data-testid="nav-tools-trigger"
        className="inline-flex items-center gap-1 font-accent text-base px-3 py-2 hover:bg-[#0a0a0a] hover:text-[#FFE600] transition-colors"
      >
        Tools <ChevronDown size={14} strokeWidth={3} />
      </button>
      <div className="invisible group-hover:visible group-focus-within:visible absolute left-1/2 -translate-x-1/2 top-full pt-2 z-50">
        <div className="min-w-[320px] bg-[#FDFBF7] border-4 border-[#0a0a0a] shadow-brutal p-2">
          {toolsMenu.map((it) => (
            <a
              key={it.label}
              href={it.href}
              data-testid={`nav-tools-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-[#FFE600] border-2 border-transparent hover:border-[#0a0a0a] transition-colors"
            >
              <div className="flex flex-col min-w-0">
                <span className="font-display text-[15px] leading-tight">
                  {it.label}
                </span>
                <span className="font-mono-body text-[11px] text-[#0a0a0a]/65 leading-tight">
                  {it.caption}
                </span>
              </div>
              {it.tag && (
                <span className="font-accent text-[10px] bg-[#0a0a0a] text-[#FFE600] px-2 py-0.5 whitespace-nowrap">
                  {it.tag}
                </span>
              )}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { price } = usePrice();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 border-b-4 border-[#0a0a0a] bg-[#FDFBF7] ${
        scrolled ? "shadow-[0_4px_0_0_rgba(10,10,10,1)]" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <a
          href="/"
          data-testid="nav-logo"
          className="flex items-center gap-2 font-display text-xl md:text-2xl tracking-tight"
        >
          <img
            src="/jobhacki-logo.png"
            alt="JobHacki"
            className="h-9 w-9 md:h-10 md:w-10 border-2 border-[#0a0a0a] rounded-md object-cover"
          />
          <span className="hidden sm:inline">JOBHACKI</span>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          <a
            href="/playbooks"
            data-testid="nav-playbooks"
            className="font-accent text-base px-3 py-2 hover:bg-[#0a0a0a] hover:text-[#FFE600] transition-colors"
          >
            Playbooks
          </a>
          <a
            href="/jobs"
            data-testid="nav-jobs"
            className="font-accent text-base px-3 py-2 hover:bg-[#0a0a0a] hover:text-[#FFE600] transition-colors"
          >
            Jobs
          </a>
          <ToolsDropdown />
          <a
            href="/#comm"
            data-testid="nav-community"
            className="font-accent text-base px-3 py-2 hover:bg-[#0a0a0a] hover:text-[#FFE600] transition-colors"
          >
            Community
          </a>
          <a
            href="/#faq"
            data-testid="nav-faq"
            className="font-accent text-base px-3 py-2 hover:bg-[#0a0a0a] hover:text-[#FFE600] transition-colors"
          >
            FAQ
          </a>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="/login"
            data-testid="nav-login"
            className="font-accent text-base px-3 py-2 hover:underline underline-offset-4 decoration-4 decoration-[#0055FF]"
          >
            Log in
          </a>
          <a
            href="https://whop.com/checkout/plan_hfsvR7lsOjQds"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-lock"
            className="hover-pop inline-flex items-center gap-2 bg-[#0055FF] text-white border-2 border-[#0a0a0a] px-4 py-2.5 font-accent text-base shadow-brutal-sm"
          >
            Lock {price} <ArrowUpRight size={16} strokeWidth={3} />
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="md:hidden w-11 h-11 border-2 border-[#0a0a0a] flex items-center justify-center bg-[#FFE600]"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t-4 border-[#0a0a0a] bg-[#FDFBF7]"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {[
              { label: "Playbooks", href: "/playbooks" },
              { label: "Jobs", href: "/jobs" },
              { label: "Resume", href: "/resume" },
              { label: "Toolbox", href: "/toolbox" },
              { label: "Simulator", href: "/simulator" },
              { label: "OmniCut", href: "/omnicut" },
              { label: "Affiliate", href: "/affiliate" },
              { label: "Community", href: "/#comm" },
              { label: "FAQ", href: "/#faq" },
            ].map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-accent text-lg py-2 border-b-2 border-dashed border-[#0a0a0a]/30"
              >
                {l.label}
              </a>
            ))}
            <a
              href="https://whop.com/checkout/plan_hfsvR7lsOjQds"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-3 bg-[#0055FF] text-white border-2 border-[#0a0a0a] px-4 py-3 font-accent text-base shadow-brutal-sm text-center"
            >
              LOCK {price}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
